// Şub 27, 2026 — DPD Tur Detay Drawer (Task 1)
// Günlük Özet'te bir tur satırına tıklayınca açılan sağ Sheet drawer.
// 3 sekme: Ham Veriler | Turun İstatistikleri | Şoförün Tarihsel Performansı
import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { Package, MapPin, Clock, TrendingUp, Award, Euro } from 'lucide-react';
import { toast } from 'sonner';

export default function DPDTourDetailDrawer({ open, onOpenChange, firmId, date, tour }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !firmId || !date || !tour) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/dpd/tour-detail?firm_id=${firmId}&date=${date}&tour=${tour}`);
        setData(r.data?.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Detay yüklenemedi');
      } finally {
        setLoading(false);
      }
    })();
  }, [open, firmId, date, tour]);

  const stats = data?.stats;
  const raw = data?.raw;
  const hist = data?.historical;
  const retour = data?.retour_by_code || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="dpd-tour-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-base px-2 py-0.5">{tour}</Badge>
            <span className="text-base">{raw?.courier_name || 'Tur Detayı'}</span>
            <span className="text-xs text-gray-500 font-normal">· {date}</span>
          </SheetTitle>
          <SheetDescription className="text-xs">
            Ham DPD Excel verisi, tur istatistikleri ve şoförün son 90 günlük performansı.
          </SheetDescription>
        </SheetHeader>
        {loading ? (
          <div className="space-y-3 mt-4">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : data ? (
          <Tabs defaultValue="stats" className="mt-4">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="stats" data-testid="drawer-tab-stats">İstatistikler</TabsTrigger>
              <TabsTrigger value="raw" data-testid="drawer-tab-raw">Ham Veri</TabsTrigger>
              <TabsTrigger value="historical" data-testid="drawer-tab-hist">Şoför Tarihsel</TabsTrigger>
            </TabsList>

            {/* Sekme 1 — Turun İstatistikleri */}
            <TabsContent value="stats" className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <MiniStat icon={Package} label="Paket" value={stats?.parcels} accent="green" />
                <MiniStat icon={MapPin} label="Stops" value={stats?.stops} accent="blue" />
                <MiniStat icon={TrendingUp} label="Teslim %" value={`%${stats?.delivery_rate ?? 0}`} accent="emerald" />
                <MiniStat icon={Package} label="Retour" value={stats?.retour} accent="red" />
                <MiniStat icon={Clock} label="Çalışma" value={`${stats?.hours ?? 0}h`} accent="indigo" />
                <MiniStat icon={Euro} label="Gelir (€2.39/paket)" value={`€${stats?.revenue_eur ?? 0}`} accent="amber" />
                <MiniStat icon={Package} label="Paket/Saat" value={stats?.parcels_per_hour} accent="purple" />
                <MiniStat icon={MapPin} label="Stops/Saat" value={stats?.stops_per_hour} accent="cyan" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded border bg-slate-50 dark:bg-slate-900/40">
                  <div className="text-gray-500">Başlangıç → Bitiş</div>
                  <div className="font-mono font-semibold">{stats?.start_time || '-'} → {stats?.end_time || '-'}</div>
                </div>
                <div className="p-2 rounded border bg-slate-50 dark:bg-slate-900/40">
                  <div className="text-gray-500">Km/Paket</div>
                  <div className="font-semibold">{stats?.km_per_parcel} km</div>
                </div>
              </div>
              {retour.length > 0 && (
                <div className="border rounded p-2">
                  <div className="text-xs font-semibold mb-1.5">Retour Sebep Dağılımı ({stats.retour})</div>
                  <div className="flex flex-wrap gap-1">
                    {retour.slice(0, 8).map((rc) => (
                      <Badge key={rc.code} variant="outline" className="text-[10px]">
                        {rc.code} · {rc.label} ({rc.count})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Sekme 2 — Ham Veri (Excel sütunları) */}
            <TabsContent value="raw" className="mt-4">
              <div className="rounded border overflow-hidden">
                <table className="w-full text-[11px]">
                  <tbody>
                    {Object.entries(raw || {})
                      .filter(([k]) => !['_id', 'id', 'ingested_at', 'ingested_by', 'mail_archive_id', 'source_filename'].includes(k))
                      .map(([k, v]) => (
                        <tr key={k} className="border-b last:border-b-0 hover:bg-slate-50 dark:hover:bg-slate-900/40">
                          <td className="px-2 py-1 text-gray-600 dark:text-gray-400 font-medium w-1/2">{k}</td>
                          <td className="px-2 py-1 font-mono text-slate-800 dark:text-slate-200 truncate">
                            {v === null || v === undefined || v === '' ? <span className="text-gray-400">—</span> : String(v)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Sekme 3 — Şoförün Tarihsel Performansı */}
            <TabsContent value="historical" className="mt-4 space-y-3">
              {hist ? (
                <>
                  <div className="text-[11px] text-gray-500">
                    Son {hist.days} çalışma günü · {hist.range}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <MiniStat icon={Package} label="Toplam Paket" value={hist.total_parcels} accent="green" />
                    <MiniStat icon={Package} label="Ort. Paket/Gün" value={hist.avg_parcels_per_day} accent="blue" />
                    <MiniStat icon={Clock} label="Toplam Saat" value={`${hist.total_hours}h`} accent="indigo" />
                    <MiniStat icon={Clock} label="Ort. Saat/Gün" value={`${hist.avg_hours_per_day}h`} accent="purple" />
                    <MiniStat icon={Award} label="En İyi Gün" value={`${hist.best_day?.parcels} pkg · ${hist.best_day?.date?.slice(5)}`} accent="emerald" />
                    <MiniStat icon={Award} label="En Düşük" value={hist.worst_day ? `${hist.worst_day.parcels} pkg · ${hist.worst_day.date?.slice(5)}` : '-'} accent="red" />
                  </div>
                  {hist.trend_series?.length > 0 && (
                    <div className="border rounded p-2" style={{ height: 220 }}>
                      <ResponsiveContainer>
                        <LineChart data={hist.trend_series} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} tick={{ fontSize: 9 }} />
                          <YAxis tick={{ fontSize: 9 }} />
                          <Tooltip contentStyle={{ fontSize: 11 }} />
                          <Legend wrapperStyle={{ fontSize: 10 }} />
                          <Line type="monotone" dataKey="parcels" name="Paket" stroke="#4f46e5" strokeWidth={2} dot={{ r: 2 }} />
                          <Line type="monotone" dataKey="hours" name="Saat" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} yAxisId={0} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-500 py-6 text-center">Şoför için tarihsel veri yok.</p>
              )}
            </TabsContent>
          </Tabs>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function MiniStat({ icon: Icon, label, value, accent }) {
  const colors = {
    green: 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300',
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
    emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
    indigo: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300',
    amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300',
    purple: 'bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300',
    cyan: 'bg-cyan-50 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-300',
  };
  return (
    <div className={`rounded-lg p-2 ${colors[accent]}`}>
      <div className="flex items-center gap-1 text-[10px] opacity-80">
        <Icon className="h-3 w-3" />{label}
      </div>
      <div className="text-base font-semibold mt-0.5 truncate">{value ?? '-'}</div>
    </div>
  );
}
